import { useContext, useEffect, useMemo, useRef, useState } from 'react';

import { useMutation } from '@apollo/client';
import { Box, Button, Flex, Icon, Input, Text, Tooltip, useDisclosure } from '@chakra-ui/react';
import { useFormikContext } from 'formik';
import { AiFillCalculator } from 'react-icons/ai';
import { toast } from 'react-toastify';

import { isElectricVehicle } from '../../../gql/carGql';
import { Deal } from '../../../gql/dealGql';
import {
  selectDealProductVscRateMutation,
  setDealProductManualTermMutation,
} from '../../../gql/dealProductGql';
import { ProductSlug, Rate, useVscRatesQuery } from '../../../gql/generated/graphql';
import { PEN_UNAVAILABLE_ERROR_CODE, hasGraphqlErrorCode } from '../../../utils/graphqlErrors';

import Modal from '../../shared/Modal';
import { getTermName } from '../constants';
import { VscPackageTypeEnum } from '../types';

import VscPicker from './VscPicker';
import './index.scss';

import { isUneditable } from '../../../constants/dealStates';
import { PermissionEnum } from '../../../constants/permissions';
import { Can } from '../../../libs/Can';
import { DealContext } from '../../../libs/DealContext';
import { AbilityContext } from '../../../libs/contextLib';
import { isNumber } from '../../../libs/utils';
import { parseVscTermString } from '../../../utils/vscTerm';
import Loader from '../../Loader/Loader';

const ALLY_LIMITED_WARRANTY_COVERAGE = 'Ally Limited Warranty';

type AllyLimitedWarrantyInfo = {
  termMonths: number;
  termMiles: number;
};

const VscRatesModal = ({
  onRatesLoaded,
  isSavingProductPrices = false,
}: {
  onRatesLoaded?: () => void;
  // True while the product price/cost auto-save (patchDealProductPricesV2) that a
  // grid pick triggers is in flight — kept separate from the Save button's own
  // updateDealProductsV2 call so the button reflects both.
  isSavingProductPrices?: boolean;
}) => {
  const { values, setFieldValue } = useFormikContext<Deal>();
  const { setUserChangedSomething } = useContext(DealContext);
  const abilities = useContext(AbilityContext);
  const canOverridePen = abilities.has(PermissionEnum.OverridePenValues);

  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [vscRates, setVscRates] = useState<Rate[]>([]);
  const [penUnavailable, setPenUnavailable] = useState<boolean>(false);
  const [allyLimitedWarranty, setAllyLimitedWarranty] = useState<AllyLimitedWarrantyInfo | null>(
    null,
  );
  const persistedTerm = useMemo(
    () => parseVscTermString(values.financial_info?.vsc_term),
    [values.financial_info?.vsc_term],
  );
  const [manualTermMonths, setManualTermMonths] = useState<string>(
    persistedTerm ? String(persistedTerm.months) : '',
  );
  const [manualTermMiles, setManualTermMiles] = useState<string>(
    persistedTerm ? String(persistedTerm.miles) : '',
  );
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Only re-seed the inputs on a closed → open transition (e.g. the rep
  // re-opens the modal) so an in-flight autosave that rewrites
  // financial_info.vsc_term doesn't stomp on the values the rep is actively
  // editing. Skipping this guard would make the form blink back to the
  // persisted term on every Formik update.
  const prevOpenRef = useRef(false);
  useEffect(() => {
    if (isOpen && !prevOpenRef.current && persistedTerm) {
      setManualTermMonths(String(persistedTerm.months));
      setManualTermMiles(String(persistedTerm.miles));
    }
    prevOpenRef.current = isOpen;
  }, [isOpen, persistedTerm]);

  const [setManualTerm, { loading: savingManualTerm }] = useMutation(
    setDealProductManualTermMutation,
  );

  const [selectVscRate] = useMutation(selectDealProductVscRateMutation);

  // Only clears VSC fields — GAP has its own success/failure path in
  // CalcGapButton.onError, and clobbering its fields here would erase a GAP
  // rate the rep just calculated.
  const resetVscFields = () => {
    setFieldValue('financial_info.vsc_price', null);
    setFieldValue('financial_info.vsc_cost', null);
    setFieldValue('financial_info.pen_vsc_session_id', null);
    setFieldValue('financial_info.pen_vsc_rate_id', null);
    setFieldValue('financial_info.pen_vsc_form_id', null);
  };

  const { loading, error: queryError } = useVscRatesQuery({
    skip: !isOpen,
    fetchPolicy: 'no-cache',
    variables: {
      bank: values.financial_info.bank ?? '',
      deal_id: values.id ?? 0,
      data: {
        year: parseInt(values.car.year, 10),
        vin: values.car.vin,
        mileage: values.car.mileage,
        msrp: values.car.retail_book_value,
        purchasePrice: values.car.payoff.vehicle_payoff,
        apr: values.financial_info.sell_rate,
        financeTerms: values.financial_info.term,
        financeTermsMileage: 0,
        financedAmount: values.car.payoff.vehicle_payoff,
        state: values.customer.address?.state,
      },
    },
    onCompleted: (data) => {
      setPenUnavailable(false);
      const allRates = (data?.vscRates ?? []) as Rate[];

      // ALW is auto-included free and arrives in the same PEN payload as the
      // selectable VSC tiers. Surface its term/miles separately and keep it out
      // of the selectable rate matrix so a rep can't pick it as a paid VSC.
      const alw = allRates.find((rate) => rate.coverage_name === ALLY_LIMITED_WARRANTY_COVERAGE);
      setAllyLimitedWarranty(
        alw
          ? { termMonths: Number(alw.term_months) || 0, termMiles: Number(alw.term_miles) || 0 }
          : null,
      );

      const selectableRates = allRates.filter(
        (rate) => rate.coverage_name !== ALLY_LIMITED_WARRANTY_COVERAGE,
      );
      // Always update so a later fetch with no selectable rates clears any
      // stale matrix from a previous open (the modal stays mounted).
      setVscRates(selectableRates);
      if (selectableRates.length) {
        setUserChangedSomething(true);
      }
    },
    onError: (error) => {
      const isPenDown = hasGraphqlErrorCode(error, PEN_UNAVAILABLE_ERROR_CODE);
      setPenUnavailable(isPenDown);
      // Manager override path: keep any manually-entered values so we don't
      // stomp work the manager already did. Only reset on non-PEN failures
      // or when the user can't manually override anyway.
      if (!isPenDown || !canOverridePen) {
        resetVscFields();
      }
      if (isPenDown && !canOverridePen) {
        toast.error('Cannot get VSC rates. Contact your manager for assistance.');
        return;
      }
      toast.error(error?.message || 'Failed to retrieve rates');
    },
  });

  const handleManualTermSubmit = async () => {
    if (values.id == null) {
      toast.error('Deal not loaded.');
      return;
    }
    const months = Number(manualTermMonths);
    const miles = Number(manualTermMiles);
    if (!Number.isFinite(months) || months <= 0) {
      toast.error('Term months must be greater than 0.');
      return;
    }
    if (!Number.isFinite(miles) || miles <= 0) {
      toast.error('Term miles must be greater than 0.');
      return;
    }
    try {
      await setManualTerm({
        variables: {
          input: {
            dealId: Number(values.id),
            slug: ProductSlug.Vsc,
            termMonths: months,
            termMiles: miles,
          },
        },
      });
      setFieldValue('financial_info.vsc_term', getTermName(months, miles));
      setFieldValue('financial_info.vsc_type', VscPackageTypeEnum.Major);
      toast.success('Manual VSC term saved.');
      onRatesLoaded?.();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to save manual VSC term';
      toast.error(message);
    }
  };

  useEffect(() => {
    setIsDisabled(
      !values.car.year ||
        !values.car.vin ||
        !values.car.mileage ||
        !isNumber(values.car.retail_book_value) ||
        !isNumber(values.car.payoff.vehicle_payoff) ||
        !isNumber(values.financial_info.sell_rate) ||
        !isNumber(values.financial_info.term) ||
        !values.customer.address?.state,
    );
  }, [values.customer.address, values.car, values.financial_info]);

  const manualTermSubmitDisabled =
    !(Number(manualTermMonths) > 0) || !(Number(manualTermMiles) > 0);
  const renderFooter = () => {
    if (penUnavailable && canOverridePen) {
      return (
        <Button
          onClick={handleManualTermSubmit}
          isLoading={savingManualTerm}
          isDisabled={manualTermSubmitDisabled}
          float="right"
        >
          SAVE MANUAL TERM
        </Button>
      );
    }
    if (!loading && !queryError && vscRates?.length) {
      // Disable while EITHER write is in flight: the Save's own
      // updateDealProductsV2 (isSaving) or the grid pick's price/cost auto-save
      // (isSavingProductPrices). Otherwise the rep can click Save mid-write.
      const saveInFlight = isSaving || isSavingProductPrices;
      return (
        <Button
          onClick={async () => {
            if (saveInFlight) {
              return;
            }
            setIsSaving(true);
            try {
              // Persist the picked VSC rate directly by penRateId so it writes to
              // deal_products even when the selected payment option doesn't include
              // VSC. The package re-persist in onRatesLoaded only submits the
              // selected package's products and would otherwise drop the pick.
              const penRateId = values.financial_info.pen_vsc_rate_id;
              if (penRateId != null && values.id != null) {
                await selectVscRate({
                  variables: { input: { dealId: Number(values.id), penRateId } },
                });
              }
              // Wait for updateDealProductsV2 (and its deal_products refetch) to
              // finish before closing, so a second click can't fire another save
              // and nothing reads deal_products mid-write.
              await onRatesLoaded?.();
              onClose();
            } catch {
              toast.error('Failed to save VSC rate. Please try again.');
            } finally {
              setIsSaving(false);
            }
          }}
          isLoading={saveInFlight}
          isDisabled={saveInFlight}
          float="right"
        >
          SAVE
        </Button>
      );
    }
    return null;
  };

  const renderTooltip = (
    <span>
      Missing:
      {!values.car.year ? <div>Car Year</div> : ''}
      {!values.car.vin ? <div>VIN</div> : ''}
      {!values.car.mileage ? <div>Mileage</div> : ''}
      {!isNumber(values.car.retail_book_value) ? <div>KBB Retail</div> : ''}
      {!isNumber(values.car.payoff.vehicle_payoff) ? <div>Payoff</div> : ''}
      {!isNumber(values.financial_info.sell_rate) ? <div>Sell Rate</div> : ''}
      {!isNumber(values.financial_info.term) ? <div>Term</div> : ''}
      {!values.customer.address?.state ? <div>State</div> : ''}
    </span>
  );

  return (
    <Can I={PermissionEnum.CalculateVSC}>
      <Tooltip
        hasArrow
        placement="right"
        label={isDisabled ? renderTooltip : ''}
        bgColor="red"
        openDelay={250}
        closeDelay={400}
      >
        <Box>
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Icon as={AiFillCalculator} fontSize="md" />}
            isDisabled={isDisabled}
            hidden={isUneditable(values.state)}
            onClick={onOpen}
          >
            CALC VSC RATES
          </Button>
        </Box>
      </Tooltip>
      <Modal
        title="VSC Rates"
        isOpen={isOpen}
        onClose={onClose}
        variant="fullContent"
        closeOnOverlayClick={false}
        size="5xl"
        rightButtons={renderFooter()}
      >
        <Box py={2} bgColor="queenBlue" fontSize={14} textAlign="center" textColor="white">
          <Text fontSize={18}>
            {isElectricVehicle(values.car.fuel_type) ? 'Ally Major Guard EV' : 'Ally Major Guard'}
          </Text>
          $200 Deductible
        </Box>
        {allyLimitedWarranty && (
          <Box py={2} px={3} bgColor="backgroundGray" fontSize={14} textAlign="center">
            <Text fontWeight="medium">
              Ally Limited Warranty {allyLimitedWarranty.termMonths} Mos /{' '}
              {allyLimitedWarranty.termMiles.toLocaleString()} Miles (included)
            </Text>
          </Box>
        )}
        {loading ? (
          <Box h="75px" bgColor="backgroundGray" borderBottomRadius="10px">
            <Loader isLoading={loading} />
          </Box>
        ) : (
          <VscPicker options={vscRates} />
        )}
        {penUnavailable && canOverridePen && (
          <Box mt={4} p={4} borderWidth="1px" borderRadius="md">
            <Text fontWeight="semibold" mb={1}>
              Manual PEN Override
            </Text>
            <Text fontSize="sm" mb={3} color="gray.600">
              PEN is unavailable. Enter the VSC term manually. Price and cost stay editable in the
              Products section.
            </Text>
            <Flex gap={3} flexWrap="wrap">
              <Box flex="1" minW="140px">
                <Text fontSize="xs" mb={1} fontWeight="medium">
                  Term Months
                </Text>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  placeholder="e.g. 60"
                  value={manualTermMonths}
                  onChange={(e) => setManualTermMonths(e.target.value)}
                  aria-label="Manual VSC term months"
                />
              </Box>
              <Box flex="1" minW="140px">
                <Text fontSize="xs" mb={1} fontWeight="medium">
                  Term Miles
                </Text>
                <Input
                  type="number"
                  inputMode="numeric"
                  min={1}
                  placeholder="e.g. 75000"
                  value={manualTermMiles}
                  onChange={(e) => setManualTermMiles(e.target.value)}
                  aria-label="Manual VSC term miles"
                />
              </Box>
            </Flex>
          </Box>
        )}
      </Modal>
    </Can>
  );
};

export default VscRatesModal;
